import React, { useEffect, useMemo, useState } from 'react';
import './CreateUTTableModal.css';

// Resolve a dropdown ID → matched DDL collection via either the
// dropdown-registrations settings ("configs[id].collectionId") OR the
// collection's `assignedTo` array. We support both for flexibility.
function resolveCollection(dropdownId, collections, regConfigs) {
  const cfg = regConfigs?.[dropdownId];
  if (cfg?.collectionId) {
    const byId = collections.find((c) => c.id === cfg.collectionId);
    if (byId) return { collection: byId, config: cfg };
  }
  const byAssigned = collections.find((c) => {
    const a = c.assignedTo;
    if (!a) return false;
    return Array.isArray(a) ? a.includes(dropdownId) : a === dropdownId;
  });
  return byAssigned ? { collection: byAssigned, config: cfg || {} } : null;
}

function sortLabels(items) {
  const pinned = ['not applicable', 'not available'];
  const isPin = (l) => pinned.includes((l || '').trim().toLowerCase());
  const top = items.filter((it) => !isPin(it.label)).sort((a, b) => a.label.localeCompare(b.label));
  const bot = items.filter((it) => isPin(it.label));
  return [...top, ...bot];
}

// Compute the original heading from DDL tertiary level under the selected
// component → measurement path. Falls back to "{component} - {measurement}".
function computeHeading(component, measurement, componentItems, regConfigs,
                       measurementDropdownId, componentDropdownId) {
  const fallback = `${component} - ${measurement}`;
  const cfg = regConfigs?.[measurementDropdownId];
  const inheritsFromComponent =
    cfg?.relationship === 'chain' && cfg?.pointTo === componentDropdownId;
  if (!inheritsFromComponent) return fallback;
  const parent = componentItems.find((it) => it.label === component);
  if (!parent) return fallback;
  const measNode = (parent.children || []).find((it) => it.label === measurement);
  if (!measNode) return fallback;
  const tertiary = (measNode.children || [])[0];
  return tertiary?.label || fallback;
}

export default function CreateUTTableModal({
  inspectionType,           // 'UT-OSI' | 'UT-ISI' | 'UT'
  existingTables,           // [{component, measurement, ...}] — used to block duplicates
  collections,              // DDL collections (passed in by parent — no internal subscription)
  regConfigs,               // dropdown-registrations configs
  onCancel,
  onCreate,                 // async (items: [{component, measurement, heading, originalHeading}]) => void
}) {
  // UT-ISI keeps its own DDL namespace; UT and UT-OSI share the
  // 'utosi' DDL prefix since they have identical measurement-table
  // structures and the user wants UT inspections to inherit
  // OSI-style defaults.
  const prefix = inspectionType === 'UT-ISI' ? 'utisi' : 'utosi';
  const componentDropdownId   = `${prefix}_measurementComponent`;
  const measurementDropdownId = `${prefix}_measurementType`;

  const [component, setComponent] = useState('');
  // Set of currently-selected measurement labels (for the active component)
  const [selected, setSelected] = useState(() => new Set());
  const [lastClickedIdx, setLastClickedIdx] = useState(null);
  // Staged pending items: [{component, measurement, heading, originalHeading}]
  const [pending, setPending] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Reset measurement selection when component changes
  useEffect(() => {
    setSelected(new Set());
    setLastClickedIdx(null);
  }, [component]);

  const componentResolution = useMemo(
    () => resolveCollection(componentDropdownId, collections || [], regConfigs || {}),
    [componentDropdownId, collections, regConfigs],
  );
  const componentItems = useMemo(
    () => componentResolution?.collection?.items || [],
    [componentResolution],
  );

  const measurementResolution = useMemo(
    () => resolveCollection(measurementDropdownId, collections || [], regConfigs || {}),
    [measurementDropdownId, collections, regConfigs],
  );

  const measurementOptions = useMemo(() => {
    const cfg = regConfigs?.[measurementDropdownId];
    if (cfg?.relationship === 'chain' && cfg?.pointTo === componentDropdownId) {
      const parent = componentItems.find((it) => it.label === component);
      return sortLabels(parent?.children || []);
    }
    return sortLabels(measurementResolution?.collection?.items || []);
  }, [regConfigs, measurementDropdownId, componentDropdownId, componentItems,
      component, measurementResolution]);

  // Custom+Custom is the one combination that can be added unlimited times —
  // it's how the user creates ad-hoc tables that don't fit the DDL taxonomy.
  function isCustomPair(comp, meas) {
    return (comp || '').trim().toLowerCase() === 'custom'
        && (meas || '').trim().toLowerCase() === 'custom';
  }

  // Existing table keys (from saved + pending) so duplicates are disabled.
  // Custom+Custom entries are excluded from the dedup set.
  const existingKeys = useMemo(() => {
    const keys = new Set();
    (existingTables || []).forEach((t) => {
      if (!isCustomPair(t.component, t.measurement)) {
        keys.add(`${t.component}|${t.measurement}`);
      }
    });
    pending.forEach((p) => {
      if (!isCustomPair(p.component, p.measurement)) {
        keys.add(`${p.component}|${p.measurement}`);
      }
    });
    return keys;
  }, [existingTables, pending]);

  function isMeasurementTaken(label) {
    if (isCustomPair(component, label)) return false;
    return existingKeys.has(`${component}|${label}`);
  }

  function handleMeasurementClick(label, idx, e) {
    if (isMeasurementTaken(label)) return;
    const next = new Set(selected);
    if (e.shiftKey && lastClickedIdx !== null) {
      // Range select between lastClickedIdx and idx
      const [a, b] = idx < lastClickedIdx ? [idx, lastClickedIdx] : [lastClickedIdx, idx];
      for (let i = a; i <= b; i++) {
        const opt = measurementOptions[i];
        if (opt && !existingKeys.has(`${component}|${opt.label}`)) {
          next.add(opt.label);
        }
      }
    } else {
      if (next.has(label)) next.delete(label);
      else next.add(label);
    }
    setSelected(next);
    setLastClickedIdx(idx);
  }

  function buildItemsFromSelection() {
    return Array.from(selected).map((meas) => ({
      component,
      measurement: meas,
      heading: computeHeading(component, meas, componentItems, regConfigs,
                              measurementDropdownId, componentDropdownId),
      originalHeading: computeHeading(component, meas, componentItems, regConfigs,
                                      measurementDropdownId, componentDropdownId),
    }));
  }

  function handleAdd() {
    if (!component || selected.size === 0) return;
    const items = buildItemsFromSelection();
    setPending((cur) => [...cur, ...items]);
    setSelected(new Set());
    setLastClickedIdx(null);
  }

  function handleRemovePending(idx) {
    setPending((cur) => cur.filter((_, i) => i !== idx));
  }

  async function handleSave() {
    setError('');
    // Combine pending + currently-selected
    const fromSelection = component && selected.size > 0 ? buildItemsFromSelection() : [];
    const all = [...pending, ...fromSelection];
    if (all.length === 0) {
      setError('Add or select at least one measurement.');
      return;
    }
    setSaving(true);
    try {
      await onCreate(all);
    } catch (err) {
      setError(err?.message || 'Failed to create tables.');
      setSaving(false);
    }
  }

  const componentMissing  = !componentResolution;
  const measurementMissing = !measurementResolution;

  const hasSelection = component && selected.size > 0;
  const canSave = (pending.length > 0 || hasSelection) && !saving;

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal create-ut-modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="modal-title">Create Measurement Tables</h2>

        {(componentMissing || measurementMissing) && (
          <div className="create-ut-warning">
            {componentMissing && (
              <div>
                No DDL collection registered to <code>{componentDropdownId}</code>.
              </div>
            )}
            {measurementMissing && (
              <div>
                No DDL collection registered to <code>{measurementDropdownId}</code>.
              </div>
            )}
            <div>Configure these in DB Management → DDL Collection.</div>
          </div>
        )}

        <div className="create-ut-grid">
          {/* Component (single-select) */}
          <div className="create-ut-field">
            <label className="create-ut-label">Component</label>
            <select
              className="create-ut-select"
              value={component}
              onChange={(e) => setComponent(e.target.value)}
            >
              <option value="">— Select component —</option>
              {sortLabels(componentItems).map((it) => (
                <option key={it.label} value={it.label}>{it.label}</option>
              ))}
            </select>
          </div>

          {/* Measurement (multi-select with checkboxes + shift-click range) */}
          <div className="create-ut-field">
            <label className="create-ut-label">
              Measurement {selected.size > 0 && (
                <span className="create-ut-count">({selected.size} selected)</span>
              )}
            </label>
            <div className={`create-ut-multiselect ${!component ? 'disabled' : ''}`}>
              {!component && (
                <div className="create-ut-multiselect-empty">
                  Select a component first
                </div>
              )}
              {component && measurementOptions.length === 0 && (
                <div className="create-ut-multiselect-empty">
                  No measurements available
                </div>
              )}
              {component && measurementOptions.map((opt, idx) => {
                const taken = isMeasurementTaken(opt.label);
                const checked = selected.has(opt.label);
                return (
                  <label
                    key={opt.label}
                    className={`create-ut-option ${taken ? 'taken' : ''} ${checked ? 'checked' : ''}`}
                    onClick={(e) => {
                      // Prevent default click-then-change cycle so shift works cleanly
                      e.preventDefault();
                      handleMeasurementClick(opt.label, idx, e);
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      readOnly
                      disabled={taken}
                    />
                    <span className="create-ut-option-label">{opt.label}</span>
                    {taken && <span className="create-ut-option-tag">already added</span>}
                  </label>
                );
              })}
            </div>
            <div className="create-ut-hint">Shift-click to select a range.</div>
          </div>
        </div>

        {/* Add button */}
        <div className="create-ut-add-row">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleAdd}
            disabled={!hasSelection}
          >
            Add to list
          </button>
        </div>

        {/* Pending chips section */}
        <div className="create-ut-pending">
          <div className="create-ut-pending-label">
            To be added {pending.length > 0 && <span className="create-ut-count">({pending.length})</span>}
          </div>
          {pending.length === 0 ? (
            <div className="create-ut-pending-empty">
              Nothing staged yet. Selected measurements will be saved directly, or use
              &ldquo;Add to list&rdquo; to stage and switch components.
            </div>
          ) : (
            <div className="create-ut-chips">
              {pending.map((p, i) => (
                <div key={`${p.component}|${p.measurement}|${i}`} className="create-ut-chip">
                  <span className="create-ut-chip-text">
                    {p.component} - {p.measurement}
                  </span>
                  <button
                    type="button"
                    className="create-ut-chip-remove"
                    onClick={() => handleRemovePending(i)}
                    aria-label="Remove"
                    title="Remove"
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {error && <div className="create-ut-error">{error}</div>}

        <div className="modal-actions create-ut-actions">
          <button className="btn btn-secondary" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={!canSave}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
